import { Flags } from "@/feature-flags";
import { useFlag } from "@/hooks/use-flag";
import { useMembership } from "@/hooks/use-membership";
import { useResourcePermissions } from "@/hooks/use-resource-permissions";
import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { defaultRetry, defaultThrowOnError } from "@/queries/errors";
import { encodePermissionKey, getPermissionLoader, type ResourcePermissionSet } from "@/queries/permission-loader";
import { keyWithPrincipal } from "@/queries/principal-key";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { useOrganization } from "@/queries/organization-queries";
import type { Permission } from "@/utils/permissions.data";
import { useQuery } from "@tanstack/react-query";
import { OrganizationRole, OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";
import type { ResourceType } from "gitpod-next-api/gitpod/v1/event_pb";

const PERMISSION_STALE_TIME = 5 * 60_000; // 5 minutes

/**
 * Query key for per-resource permission cache entries.
 * Prefix is used by invalidation handlers to clear all permission caches.
 */
export const permissionQueryKey = {
    resource: (resourceType: ResourceType, resourceId: string) =>
        keyWithPrincipal(["permissions", "resource", resourceType, resourceId]),
    /** Prefix for invalidating all per-resource permission caches */
    all: () => keyWithPrincipal(["permissions"]),
};

/**
 * Hook for checking a single permission on a specific resource.
 *
 * Uses a DataLoader under the hood: multiple useResourcePermission hooks
 * mounting in the same render cycle share a single batched API call.
 *
 * Gated behind the `permission_dataloader_enabled` feature flag.
 * When the flag is off, falls back to the old useResourcePermissions() hook.
 *
 * Both paths are called unconditionally to satisfy the rules of hooks.
 * The flag determines which result is returned.
 */
export function useResourcePermission(
    resourceType: ResourceType,
    resourceId: string,
    permission: Permission,
): { hasPermission: boolean; isLoading: boolean } {
    const { value: dataloaderEnabled, loading: flagLoading } = useFlag(Flags.PermissionDataloaderEnabled, false);

    // Both paths called unconditionally (rules of hooks).
    // The flag gates `enabled` so only one path makes network requests.
    // Suppress legacy while the flag is loading to avoid a wasted fetch when
    // the flag resolves to true. No loading flash because we return
    // hasPermission: false / isLoading: false during the brief flag-load window.
    const legacyEnabled = !flagLoading && !dataloaderEnabled;
    const legacy = useResourcePermissionLegacy(resourceType, resourceId, permission, legacyEnabled);
    const dataloader = useResourcePermissionDataloader(resourceType, resourceId, permission, dataloaderEnabled);

    if (!dataloaderEnabled) {
        return legacy;
    }

    return dataloader;
}

/**
 * Legacy path: delegates to the global useResourcePermissions() hook.
 */
function useResourcePermissionLegacy(
    resourceType: ResourceType,
    resourceId: string,
    permission: Permission,
    enabled: boolean,
): { hasPermission: boolean; isLoading: boolean } {
    const { hasPermission, isLoading } = useResourcePermissions({ enabled });
    return {
        hasPermission: hasPermission(resourceType, resourceId, permission),
        isLoading,
    };
}

/**
 * Dataloader path: fetches permissions per-resource via batched API calls.
 * @param `active` controls whether queries fire.
 */
function useResourcePermissionDataloader(
    resourceType: ResourceType,
    resourceId: string,
    permission: Permission,
    active: boolean,
): { hasPermission: boolean; isLoading: boolean } {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();
    const { data: organization } = useOrganization();
    const { membership } = useMembership();
    const isOrgAdmin = membership?.userRole === OrganizationRole.ADMIN;

    // Non-enterprise orgs don't use RBAC permissions
    const isEnterprise = organization?.tier === OrganizationTier.ENTERPRISE;
    const enabled = active && isEnterprise && !!membership && !isOrgAdmin && !!user?.id && !!resourceId;

    const loader = user?.id && organization?.id ? getPermissionLoader(api, user.id, organization.id) : undefined;
    const key = encodePermissionKey(resourceType, resourceId);

    const { data, isPending } = useQuery<ResourcePermissionSet>({
        queryKey: permissionQueryKey.resource(resourceType, resourceId),
        queryFn: async () => {
            if (!loader) {
                return Promise.resolve(new Set<Permission>() as ResourcePermissionSet);
            }
            const result = await loader.load(key);

            return result ?? (new Set<Permission>() as ResourcePermissionSet);
        },
        enabled,
        staleTime: PERMISSION_STALE_TIME,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });

    // Org admins have all permissions
    if (isOrgAdmin) {
        return { hasPermission: true, isLoading: false };
    }

    if (!isEnterprise) {
        return { hasPermission: false, isLoading: false };
    }

    return {
        hasPermission: data?.has(permission) ?? false,
        isLoading: isPending && enabled,
    };
}
